<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale, type SupportedLocale } from '@/i18n'

const { locale } = useI18n()
const open = ref(false)

const options: Array<{ code: SupportedLocale; label: string }> = [
  { code: 'en', label: 'EN' },
  { code: 'zh-CN', label: '中文' },
]

async function switchTo(code: SupportedLocale) {
  await setLocale(code)
  open.value = false
}

// 点击外部关闭
function onClickOutside(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!target.closest('.locale-switcher')) open.value = false
}

watch(open, (v) => {
  if (v) document.addEventListener('click', onClickOutside)
  else document.removeEventListener('click', onClickOutside)
})
</script>

<template>
  <div class="locale-switcher">
    <button
      type="button"
      class="locale-button"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click.stop="open = !open"
    >
      {{ options.find((o) => o.code === locale)?.label }}
      <span class="arrow">▾</span>
    </button>
    <ul v-if="open" class="locale-menu" role="listbox">
      <li
        v-for="opt in options"
        :key="opt.code"
        :class="{ active: opt.code === locale }"
        role="option"
        :aria-selected="opt.code === locale"
        @click="switchTo(opt.code)"
      >
        {{ opt.label }}
      </li>
    </ul>
  </div>
</template>

<style scoped>
.locale-switcher {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 100;
}
.locale-button {
  background: rgba(0, 0, 0, 0.4);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.4rem 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}
.locale-button:hover {
  background: rgba(0, 0, 0, 0.6);
}
.locale-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 0.25rem);
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.375rem;
  min-width: 5rem;
}
.locale-menu li {
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  color: white;
  font-size: 0.875rem;
}
.locale-menu li:hover {
  background: rgba(64, 224, 255, 0.2);
}
.locale-menu li.active {
  background: rgba(64, 224, 255, 0.3);
  font-weight: 600;
}
</style>
